import React, { useState } from "react";
import { cn } from "../lib/utils";
import type { Product } from "../types";
import { getDisplayTag } from "../types";
import { ImageWithSkeleton } from "./ImageWithSkeleton";

const fruitPatternImg = "/images/fruit-pattern.jpg";
const productDefaultBackImg = "/images/products/product-default-back.png";

interface ProductCardProps {
  product: Product;
  onAddToCart: (product: Product, quantity?: number) => void;
}

export function ProductCard({
  product,
  onAddToCart,
  onClick,
}: ProductCardProps & { onClick: () => void }) {
  const [isFlipped, setIsFlipped] = useState(false);

  // Only show tag if it's a valid ProductTag enum value
  const displayTag = getDisplayTag(product.tag);

  return (
    <div
      className="group relative bg-[#1a1a1a] border-2 border-white/10 hover:border-[var(--color-sansi-green)] transition-all duration-300 hover:-translate-y-2 hover:shadow-[8px_8px_0px_var(--color-sansi-orange)] cursor-pointer"
      onMouseEnter={() => setIsFlipped(true)}
      onMouseLeave={() => setIsFlipped(false)}
      onClick={onClick}
    >
      {/* Tags - Outside overflow container with high z-index */}
      <div className="absolute top-0 right-0 flex flex-col items-end z-20">
        {displayTag && (
          <span className="bg-[var(--color-sansi-orange)] text-black text-xs font-black uppercase px-3 py-1">
            {displayTag}
          </span>
        )}
        {product.offer && (
          <span className="bg-red-600 text-white text-xs font-black uppercase px-3 py-1">
            {product.offer.discount}% OFF
          </span>
        )}
      </div>

      {/* Image Container - Square Aspect Ratio */}
      <div className="relative aspect-square overflow-hidden bg-white/5 border-b-2 border-white/10 group-hover:border-[var(--color-sansi-green)] flex items-center justify-center">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20 pointer-events-none z-0"
          style={{ backgroundImage: `url(${fruitPatternImg})` }}
        />
        <ImageWithSkeleton
          src={
            isFlipped
              ? product.backImage || productDefaultBackImg
              : product.image
          }
          alt={product.name}
          className="max-w-[90%] max-h-[90%] w-auto h-auto object-contain transition-transform duration-500 group-hover:scale-110 relative z-10 mx-auto"
        />
      </div>

      {/* Content */}
      <div className="p-3 md:p-6 text-center space-y-2 md:space-y-4">
        <div className="space-y-0.5">
          <h3 className="text-sm md:text-xl font-bold text-white uppercase tracking-wide group-hover:text-[var(--color-sansi-green)] transition-colors line-clamp-2 min-h-[2.5rem] md:min-h-[3.5rem] flex items-center justify-center">
            {product.name}
          </h3>

          {product.weight && (
            <p className="text-gray-400 text-xs md:text-sm">{product.weight}</p>
          )}
        </div>

        <div className="flex flex-col gap-2 md:gap-3">
          <div className="flex flex-col items-center justify-center min-h-[3rem]">
            {product.offer && (
              <span className="text-gray-400 text-sm line-through decoration-2">
                Rs. {product.offer.originalPrice.toLocaleString()}
              </span>
            )}
            <span className="text-lg md:text-3xl font-black text-white">
              Rs. {product.price.toLocaleString()}
            </span>
          </div>

          <button
            onClick={(e) => {
              e.stopPropagation();
              onAddToCart(product);
            }}
            className="w-full bg-white text-black font-bold uppercase py-2 md:py-3 text-xs md:text-base hover:bg-[var(--color-sansi-orange)] hover:text-white transition-colors border-2 border-transparent"
            aria-label="Add to cart"
          >
            Add to Cart
          </button>
        </div>
      </div>
    </div>
  );
}
